/**
 * Returns values contained in both arrays, A and B (intersection)
 *
 * @param {Array} A Array A
 * @param {Array} B Array B
 *
 * @return {Array} resulting array
 *
 * For [1,2,3] [2,3] it will yield [2,3]. On the other hand, for [1,2,3] [2,3,5] will return the same thing.
 */
export const intersection = (A, B) => A.filter((x) => B.includes(x));

/**
 * Returns values from A that are not in B
 *
 * @param {Array} A Array A
 * @param {Array} B Array B
 *
 * @return {Array} resulting array
 *
 * For [1,2,3] [2,3] it will yield [1]. On the other hand, for [1,2,3] [2,3,5] will return the same thing.
 */
export const difference = (A, B) => A.filter((x) => !B.includes(x));

/**
 * Returns values from A and B that are only in one of the two arrays (don't belong to intersection)
 *
 * @param {Array} A Array A
 * @param {Array} B Array B
 *
 * @return {Array} resulting array
 *
 */
export const symetricDifference = (A, B) =>
  A.filter((x) => !B.includes(x)).concat(B.filter((x) => !A.includes(x)));

/**
 * "Sort by" function to use with Array.sort():
 *
 *
 *    MyArray.sort(
 *      sortBy({
 *        key: 'index'
 *      })
 *    )
 *
 *    MyArray.sort(
 *      sortBy({
 *        key: 'name',
 *        type: 'string',
 *        asc: true
 *      })
 *    )
 *
 * @param {Object} config - Configuration object (optional)
 *    @param {String}  config.key - If set, assumes that a and b are objects and will use keyed values a[key] and b[key]
 *    @param {Boolean} config.asc - Ascendent order if true, descendent if false
 *    @param {String}  config.type - If set, it will enforce var types insted of infer them
 *
 * @returns {function} Sorting function
 */
export const sortBy =
  ({ key = null, asc = true, type = null }) =>
  (a, b) => {
    const value = (v) => (key ? v[key] : v);
    const valueType = (v) => type ?? typeof value(v);
    const parsedValue = (v) =>
      valueType(v) === "string"
        ? String(value(v))?.toUpperCase()
        : valueType(v) === "number"
        ? Number(value(v))
        : value(v);

    const A = parsedValue(a);
    const B = parsedValue(b);

    return asc ? (A < B ? -1 : A > B ? 1 : 0) : A > B ? -1 : A < B ? 1 : 0;
  };

/**
 * Returns an array of unique elements
 */
export const unique = (a) =>
  a.filter((value, index, self) => self.indexOf(value) === index);
